import {isBoolean, isUndefined} from 'lodash';
import {API_CODE_NO_PERMISSION, API_CODE_USER_NOT_ACTIVE} from '../../config';

export const DEFAULT_MSG = '网络不给力,请稍后再试试吧';

/**
 * 网络关闭时候的错误码
 */
export const networkClosedCode = -100000;
/**
 * action的api请求中, 错误处理配置枚举
 */
export const apiHandleLevel = {
  warning: 1,
  error: 2
};
/**
 * 接口响应的处理State枚举
 */
const apiHandleEnum = {
  none: 0,
  ...apiHandleLevel
};

export class ApiHandleOpt {
  constructor(level = apiHandleLevel.warning, onRetry, useServerMsg = true) {
    this._checkLevel(level, onRetry);
    this._level = level;
    this._onRetry = onRetry;
    this._useServerMsg = useServerMsg;

    //config中默认配置项
    this._defaultConfigItem = {enable: true, level, useServerMsg/*, msg: undefined*/, onRetry};

    this._config = {
      100002: {enable: false}, //登录超时, 默认不处理
      [networkClosedCode]: {...this._defaultConfigItem, msg: '请启用蜂窝移动数据或Wi-Fi访问'},
      [-1]: {...this._defaultConfigItem, msg: DEFAULT_MSG},
      [API_CODE_NO_PERMISSION]: {...this._defaultConfigItem, msg: '用户没有此功能的权限， 请联系管理员开通'},
      [API_CODE_USER_NOT_ACTIVE]: {...this._defaultConfigItem, msg: '账号已被禁用，请联系管理员处理'},
    };
  }
  static create(level = apiHandleLevel.warning, onRetry, useServerMsg = true) {
    return new ApiHandleOpt(level, onRetry, useServerMsg);
  }
}
ApiHandleOpt.prototype = {
  _checkErrorCode(errorCode) {
    if (isUndefined(errorCode) || isNaN(errorCode) || errorCode === 0) {
      throw new Error('请指定errorCode为非0的错误码');
    }
  },
  _checkLevel(level, onRetry) {
    if (!(level === apiHandleLevel.warning || level === apiHandleLevel.error)) {
      throw new Error('请通过apiHandleLevel指定level参数');
    }
    if (level === apiHandleLevel.error && !onRetry) {
      throw new Error('当level为error时, 必须提供onRetry参数');
    }
  },
  skip(...errorCodes) {
    if (errorCodes.length === 0) {
      this._checkErrorCode();
    }
    errorCodes.forEach(errorCode => {
      this._checkErrorCode(errorCode);
      this._config[errorCode] = {enable: false};
    });

    return this;
  },
  setMsg(errorCode, msg) {
    this._checkErrorCode(errorCode);
    if (!msg) {
      throw new Error('请指定msg参数');
    }
    this._config[errorCode] = {...this._defaultConfigItem, msg};
    return this;
  },
  setItem(errorCode, level, onRetry, msg, useServerMsg = this._useServerMsg) {
    this._checkErrorCode(errorCode);
    this._checkLevel(level, onRetry);
    this._config[errorCode] = {...this._defaultConfigItem, ...{level, msg, useServerMsg, onRetry}};
    return this;
  }
};

/**
 * 检查action中apiHandleOpt配置
 *
 * @return {enable:bool, option:object}
 */
const checkOption = (apiHandleOpt) => {
  if (isUndefined(apiHandleOpt)) {
    return new ApiHandleOpt();
  }
  if (isBoolean(apiHandleOpt)) {
    if (apiHandleOpt) {
      return new ApiHandleOpt();
    }
    return null;
  }
  if (!(apiHandleOpt instanceof ApiHandleOpt)) {
    throw new Error('apiHandleOpt属性值必须是ApiHandleOpt实例');
  }
  return apiHandleOpt;
};

let mOnRetry;
/**
 * 设置网络错误页, 重试的Handler
 */
export const setRetryHandler = (callback) => {
  mOnRetry = callback;
};
/**
 * 获取网络错误页, 重试的Handler
 */
export const getRetryHandler = () => mOnRetry;

const createAction = (level, msg) => ({level, msg});

// 给action添加 apiHandle
const attachAction = (sourceActionObj, actionObj) => {
  if (actionObj) {
    sourceActionObj.apiHandle = actionObj;
  }
  return sourceActionObj;
};
/**
 * 重置apiHandle的action对象
 */
export const clearApiHandle = (sourceActionObj, apiHandleOpt) => {
  // 判断CALL_API.apiHandleOpt
  // apiHandleOpt => undefined boolean ApiHandleOpt实例
  // checkOption => apiHandleOpt ApiHandleOptA 实例 or null
  const checkOptionResult = checkOption(apiHandleOpt);

  if (checkOptionResult) {
    // apiHandleEnum.none => 0
    // createAction(apiHandleEnum.none, '') => {level: 0, msg: ''}
    // attachAction => {...sourceActionObj, apiHandle: {level: 0, msg: ''}}
    attachAction(sourceActionObj, createAction(apiHandleEnum.none, ''));
  }
  return checkOptionResult;
};

/**
 * 获取apiHandle的action对象, 通过retCode和action中的配置
 */
const getActionObj = (retCode, retMsg, checkOptionResult) => {
  // 本次action不做错误处理
  if (!checkOptionResult) {
    return null;
  }

  const {_level: level, _useServerMsg: useServerMsg, _onRetry: onRetry, _config: config} = checkOptionResult;
  if (!isUndefined(onRetry)) {
    setRetryHandler(onRetry);
  }

  const resultItem = config[retCode];
  if (resultItem) {
    if (!resultItem.enable) {
      return null;
    }
    resultItem.onRetry && setRetryHandler(resultItem.onRetry);
    if (resultItem.msg) {
      return createAction(resultItem.level, resultItem.msg);
    }
    if (resultItem.useServerMsg) {
      return createAction(resultItem.level, retMsg);
    }
    return createAction(resultItem.level, DEFAULT_MSG);
  }

  if (useServerMsg) {
    return createAction(level, retMsg);
  }
  // createAction => {level, msg}
  return createAction(level, DEFAULT_MSG);
};

/**
 * 获取apiHandle的值, 通过retCode和action中的配置
 */
export const processApiHandle = (sourceActionObj, retCode, retMsg, checkOptionResult) => {
  // actionObj => null or {level, msg}
  const actionObj = getActionObj(retCode, retMsg, checkOptionResult);
  // attachAction => {...sourceActionObj, apiHandle: {level, msg}}
  return attachAction(sourceActionObj, actionObj);
};



// WEBPACK FOOTER //
// ./src/lib/util/apiHandle.js